import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionContract, SubscriptionState } from '../../contracts/subscription';
import ConfirmationModal from '../Applications/ConfirmationModal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface SubscriptionCardProps {
    subscription: SubscriptionContract;
    onCancel: (subscriptionId: string) => Promise<void>;
    onRename: (subscriptionId: string, newName: string) => Promise<void>;
    onRegenerateKey: (subscriptionId: string, keyType: 'primary' | 'secondary') => Promise<void>;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
    subscription,
    onCancel,
    onRename,
    onRegenerateKey,
}) => {
    const { t, i18n } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(subscription.displayName);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState<'primary' | 'secondary' | null>(null);
    const [isCanceling, setIsCanceling] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [showPrimaryKey, setShowPrimaryKey] = useState(false);
    const [showSecondaryKey, setShowSecondaryKey] = useState(false);

    const handleCopy = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSaveName = async () => {
        if (editedName.trim() && editedName !== subscription.displayName) {
            setIsSaving(true);
            try {
                await onRename(subscription.id, editedName.trim());
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to rename:', error);
                alert(t('subscriptions.renameError'));
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleCancelEdit = () => {
        setEditedName(subscription.displayName);
        setIsEditing(false);
    };

    const handleRegenerateKey = async (keyType: 'primary' | 'secondary') => {
        setIsRegenerating(true);
        try {
            await onRegenerateKey(subscription.id, keyType);
            setShowRegenerateConfirm(null);
        } catch (error) {
            console.error(`Failed to regenerate ${keyType} key:`, error);
            alert(t('errors.withMessage', { message: `${t('subscriptions.regenerateKeyError')} ${keyType}` }));
        } finally {
            setIsRegenerating(false);
        }
    };

    const formattedDate = subscription.createdDate || subscription.startDate
        ? new Date(subscription.createdDate || subscription.startDate!).toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
        : '—';

    /**
     * Map SubscriptionState to a display label.
     * API returns uppercase strings like "ACTIVE", "CANCELLED".
     */
    const getStateLabel = (state: SubscriptionState | string): string => {
        if (!state) return 'Unknown';
        const s = String(state);
        // Convert "ACTIVE" → "Active", "CANCELLED" → "Cancelled"
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    };

    const getStateColor = (state: SubscriptionState | string): string => {
        const label = String(state).toLowerCase();
        switch (label) {
            case 'active':
                return '#10b981';
            case 'suspended':
                return '#f59e0b';
            case 'cancelled':
                return '#ef4444';
            case 'expired':
                return '#6b7280';
            case 'submitted':
                return '#3b82f6';
            case 'rejected':
                return '#dc2626';
            default:
                return '#06b6d4';
        }
    };

    return (
        <>
            {(isRegenerating || isCanceling) && (
                <div className="fullpage-loader-overlay">
                    <div className="fullpage-loader">
                        <div className="loader-spinner"></div>
                    </div>
                </div>
            )}
            <div className="subscription-card" data-sub-id={subscription.id} data-expanded={isExpanded}>
                <div className="subscription-card-header" onClick={() => setIsExpanded(!isExpanded)}>
                    <div className="subscription-header-left">
                        {isEditing ? (
                            <input
                                type="text"
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="subscription-name-input"
                                autoFocus
                            />
                        ) : (
                            <h3 className="subscription-name">{subscription.displayName}</h3>
                        )}
                    </div>
                    <div className="subscription-header-right">
                        {isEditing ? (
                            <>
                                <button
                                    className="subscription-icon-btn success"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveName();
                                    }}
                                    title={t('common.save')}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <span className="btn-loader"></span> : <CheckIcon fontSize="small" />}
                                </button>
                                <button
                                    className="subscription-icon-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelEdit();
                                    }}
                                    title={t('common.cancel')}
                                >
                                    <CloseIcon fontSize="small" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="subscription-icon-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(true);
                                    }}
                                    title={t('subscriptions.editName')}
                                >
                                    <EditIcon fontSize="small" />
                                </button>
                                {isExpanded && (
                                    <button
                                        className="subscription-icon-btn danger"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowCancelConfirm(true);
                                        }}
                                        title={t('subscriptions.cancelSubscription')}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </button>
                                )}
                            </>
                        )}
                        <button className="subscription-expand-btn">
                            {isExpanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                        </button>
                    </div>
                </div>

                {isExpanded && (
                    <div className="subscription-card-body">
                        {/* Status - Centered */}
                        <div className="subscription-status-section">
                            <span className="subscription-status-label">{t('subscriptions.status')} :</span>
                            <span
                                className="subscription-status-value"
                                style={{ color: getStateColor(subscription.state) }}
                            >
                                {getStateLabel(subscription.state)}
                            </span>
                        </div>

                        {/* Activation Date */}
                        <div className="subscription-field">
                            <label className="subscription-label">{t('subscriptions.activationDate')}</label>
                            <input
                                type="text"
                                value={formattedDate}
                                readOnly
                                className="subscription-input"
                            />
                        </div>

                        {/* Manage API Keys Section */}
                        <div className="subscription-api-keys-section">
                            <h4 className="subscription-api-keys-title">{t('subscriptions.manageApiKeys')}</h4>

                            {/* Primary Key */}
                            <div className="subscription-field">
                                <label className="subscription-label">{t('subscriptions.primaryKey')}</label>
                                <div className="subscription-key-row">
                                    <div className="subscription-input-wrapper">
                                        <input
                                            type={showPrimaryKey ? "text" : "password"}
                                            value={subscription.primaryKey || ''}
                                            readOnly
                                            className="subscription-input"
                                        />
                                        <button
                                            className={`subscription-copy-btn ${copiedField === 'primaryKey' ? 'copied' : ''}`}
                                            onClick={() => handleCopy(subscription.primaryKey || '', 'primaryKey')}
                                            title={t('subscriptions.copyPrimaryKey')}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </button>
                                        {copiedField === 'primaryKey' && (
                                            <span className="copy-success-message">{t('applications.copied')}</span>
                                        )}
                                    </div>
                                    <button 
                                        className="subscription-icon-action-btn"
                                        onClick={() => setShowPrimaryKey(!showPrimaryKey)}
                                        title={showPrimaryKey ? t('subscriptions.hideKey') : t('subscriptions.showKey')}
                                    >
                                        {showPrimaryKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </button>
                                    <button 
                                        className="subscription-icon-action-btn"
                                        onClick={() => setShowRegenerateConfirm('primary')}
                                        title={t('subscriptions.regeneratePrimaryKey')}
                                    >
                                        <RefreshIcon />
                                    </button>
                                </div>
                            </div>

                            {/* Secondary Key */}
                            <div className="subscription-field">
                                <label className="subscription-label">{t('subscriptions.secondaryKey')}</label>
                                <div className="subscription-key-row">
                                    <div className="subscription-input-wrapper">
                                        <input
                                            type={showSecondaryKey ? "text" : "password"}
                                            value={subscription.secondaryKey || ''}
                                            readOnly
                                            className="subscription-input"
                                        />
                                        <button
                                            className={`subscription-copy-btn ${copiedField === 'secondaryKey' ? 'copied' : ''}`}
                                            onClick={() => handleCopy(subscription.secondaryKey || '', 'secondaryKey')}
                                            title={t('subscriptions.copySecondaryKey')}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </button>
                                        {copiedField === 'secondaryKey' && (
                                            <span className="copy-success-message">{t('applications.copied')}</span>
                                        )}
                                    </div>
                                    <button 
                                        className="subscription-icon-action-btn"
                                        onClick={() => setShowSecondaryKey(!showSecondaryKey)}
                                        title={showSecondaryKey ? t('subscriptions.hideKey') : t('subscriptions.showKey')}
                                    >
                                        {showSecondaryKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </button>
                                    <button 
                                        className="subscription-icon-action-btn"
                                        onClick={() => setShowRegenerateConfirm('secondary')}
                                        title={t('subscriptions.regenerateSecondaryKey')}
                                    >
                                        <RefreshIcon />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Confirmation Modal */}
            <ConfirmationModal
                isOpen={showCancelConfirm}
                title={t('subscriptions.cancelSubscription')}
                message={t('subscriptions.confirmCancel')}
                onConfirm={async () => {
                    setIsCanceling(true);
                    try {
                        await onCancel(subscription.id);
                    } finally {
                        setIsCanceling(false);
                        setShowCancelConfirm(false);
                    }
                }}
                onCancel={() => setShowCancelConfirm(false)}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                isLoading={isCanceling}
            />

            {/* Regenerate Key Confirmation Modal */}
            {showRegenerateConfirm && (
                <ConfirmationModal
                    isOpen={true}
                    title={t('subscriptions.regenerateKeyTitle', { 
                        keyType: showRegenerateConfirm === 'primary' ? t('subscriptions.primary') : t('subscriptions.secondary')
                    })}
                    message={t('subscriptions.regenerateKeyMessage')}
                    onConfirm={() => handleRegenerateKey(showRegenerateConfirm)}
                    onCancel={() => setShowRegenerateConfirm(null)}
                    confirmText={t('subscriptions.yesRegenerate')}
                    cancelText={t('common.cancel')}
                    isLoading={isRegenerating}
                />
            )}
        </>
    );
};

export default SubscriptionCard;
